import { tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { computed } from "@ember/object"
import type { Planbook } from "@shared/models/planbook"
import type { PlanbookCalendarDateCombined } from "@shared/models/planbook-calendar-date-combined"
import type {
  RotationCalendar,
  RotationCalendarDateAnnotationSingle,
} from "@shared/models/rotation-calendar"
import {
  findPlanbookCustomDate,
  findPlanbookDefaultDate,
} from "cc-frontend/utils/filter-dates/planbook"
import { filter, find, includes, isNil, map, max, min } from "lodash-es"

@tagName("")
export default class PlanbookDateCombinedComponent extends Component {
  // args
  dateString!: string
  rotationCalendar!: RotationCalendar
  planbook!: Planbook

  @computed(
    "dateString",
    "planbook.attributes.calendar.dateAnnotations.[]",
    "planbook.attributes.calendar.dates.[]",
    "planbook.attributes.calendar.datesOff.[]",
    "planbook.attributes.calendar.version",
    "rotationCalendar.attributes.dateAnnotations.[]",
    "rotationCalendar.attributes._revision"
  )
  get planbookDateCombined(): PlanbookCalendarDateCombined | null {
    if (isNil(this.rotationCalendar)) return null
    let dateString = this.dateString
    let rotationDate = find(
      this.rotationCalendar.attributes.dates,
      (date) => date.attributes.dateString === dateString
    )
    let planbookDateCustom = findPlanbookCustomDate(this.planbook, dateString)
    let planbookDateDefault = findPlanbookDefaultDate(this.planbook, {
      rotationId: rotationDate?.attributes.rotationId,
      semesterId: rotationDate?.attributes.semesterId,
    })
    let firstDay = min(map(this.rotationCalendar.attributes.semesters, "startDate"))
    let lastDay = max(map(this.rotationCalendar.attributes.semesters, "endDate"))
    if (firstDay === undefined || lastDay === undefined) throw Error("No start or end date")

    let planbookDateDefaultIsOff = includes(
      this.planbook.attributes.calendar.datesOff,
      `${rotationDate?.attributes.semesterId}:${rotationDate?.attributes.rotationId}`
    )
    let planbookDateCustomIsOff = includes(this.planbook.attributes.calendar.datesOff, dateString)
    let isOff = planbookDateCustomIsOff ? planbookDateCustomIsOff : planbookDateDefaultIsOff

    let dateAnnotations = filter(
      this.planbook.attributes.calendar.dateAnnotations,
      (annotation) => {
        if (annotation.type === "date-annotation-planbook-custom-single") {
          return annotation.attributes.dateString === dateString
        }
        if (annotation.type === "date-annotation-planbook-custom-range") {
          return (
            annotation.attributes.startDate <= dateString &&
            annotation.attributes.endDate >= dateString
          )
        }
        return false
      }
    )

    let daysBeforeFirstDay = dateFns.differenceInDays(
      dateFns.parse(firstDay),
      dateFns.parse(dateString)
    )
    let daysAfterLastDay = dateFns.differenceInDays(
      dateFns.parse(dateString),
      dateFns.parse(lastDay)
    )

    let cardStackId = planbookDateCustom ? planbookDateCustom.attributes.cardStackId : null

    let rotationCalendarException = find(
      this.rotationCalendar.attributes.dateAnnotations,
      (annotation) => {
        return (
          annotation.type === "date-annotation-rotation-calendar-single" &&
          annotation.attributes.dateString === dateString &&
          (annotation.attributes.rotationShould === "CHANGE_AND_CYCLE_SHOULD_RESET" ||
            annotation.attributes.rotationShould ===
              "CHANGE_AND_CYCLE_SHOULD_BUMP_OVER_DISRUPTION" ||
            annotation.attributes.rotationShould ===
              "CHANGE_AND_CYCLE_SHOULD_CONTINUE_UNINTERRUPTED")
        )
      }
    ) as RotationCalendarDateAnnotationSingle | undefined

    let rotation = find(
      this.rotationCalendar.attributes.rotations,
      (rotation) => rotation.id === rotationDate?.attributes.rotationId
    )

    let rotationDayTitle = isNil(rotation) !== true ? rotation?.title : ""

    return {
      planbookDateId: planbookDateCustom ? planbookDateCustom.id : null,
      rotationCalendarException: rotationCalendarException,
      dateString: dateString,
      cardStackId: cardStackId,
      isBlockSchedule: this.rotationCalendar.attributes.rotationType !== "weekly",
      isUpdatingFromTemplate: cardStackId === null,
      rotationDayTitle: rotationDayTitle,
      cardStackTemplateId: planbookDateDefault ? planbookDateDefault.attributes.cardStackId : null,
      isBeforeSchoolYear: dateString < firstDay,
      isAfterSchoolYear: dateString > lastDay,
      isInsideSchoolYear: dateString >= firstDay && dateString <= lastDay,
      firstDayOfSchoolYear: firstDay,
      lastDayOfSchoolYear: lastDay,
      daysBeforeFirstDay: daysBeforeFirstDay,
      daysAfterLastDay: daysAfterLastDay,
      isOff: isOff,
      dateAnnotations: dateAnnotations,
    }
  }
}
